import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';

import { reportsAPI } from '../services/api';
import { tablePageHeaderStickySx, tablePageRootSx } from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { downloadBlob } from '../utils/download';
import { createPrefixFilter } from '../utils/autocompleteFilters';

const ReportsPage = () => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loadingYears, setLoadingYears] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadYears = async () => {
      try {
        const response = await reportsAPI.getTaxYears();
        const available = toArray(response.data, 'tax years');
        setYears(available);
        if (available.length > 0) {
          setSelectedYear(String(available[0]));
        }
      } catch (err) {
        console.error('Error loading tax years:', err);
        setError('Could not load the list of available years.');
      } finally {
        setLoadingYears(false);
      }
    };
    loadYears();
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selectedYear) return;
    setDownloading(true);
    setError('');
    try {
      const response = await reportsAPI.downloadTaxYear(selectedYear);
      downloadBlob(response.data, `Taxes_${selectedYear}.xlsx`);
    } catch (err) {
      console.error('Error downloading tax report:', err);
      setError('Could not generate the report. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [selectedYear]);

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Typography variant="h4" sx={{ pt: 2, pb: 1 }}>
          Tax Workbook
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card sx={{ maxWidth: 720 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="h6">Tax Year Workbook</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              An Excel workbook for your tax preparer: a Summary sheet totaling every
              income and expense type by month, backed by twelve monthly sheets listing
              each individual transaction.
            </Typography>

            {loadingYears ? (
              <CircularProgress size={24} />
            ) : years.length === 0 ? (
              <Alert severity="info">
                No income or expenses have been recorded yet, so there is nothing to
                report on.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                  size="small"
                  disableClearable
                  sx={{ minWidth: 160 }}
                  options={years}
                  getOptionLabel={(year) => String(year)}
                  filterOptions={createPrefixFilter((year) => String(year))}
                  value={years.find((year) => String(year) === selectedYear) ?? years[0]}
                  onChange={(e, newValue) => setSelectedYear(String(newValue))}
                  renderInput={(params) => <TextField {...params} label="Tax Year" />}
                />

                <Button
                  variant="contained"
                  startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={downloading || !selectedYear}
                >
                  {downloading ? 'Generating…' : 'Download Excel'}
                </Button>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Shared household costs are prorated by the home-office percentage set on the
              Business Info page, for the expense types marked “prorate” under Expense Types.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ReportsPage;
